import { OnboardingStatus, OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { useLocation } from 'react-router'
import { getAllStepConfigs, OnboardingStepConfig, STEP_ROUTE_MAPPING } from '../onboarding/stepConfiguration'
import { HealthcheckStatus } from '../types'

// Extract the onboarding workflow type from the organization fragment
type OnboardingWorkflowFragment = NonNullable<OrganizationDetailFragment['onboardingWorkflow']>
type OnboardingStepFragment = OnboardingWorkflowFragment['steps'][0]
type UserFragment = OnboardingStepFragment['assignee']

export const ORDERED_ONBOARDING_STEP_TYPES: OnboardingStepType[] = [
  OnboardingStepType.OrderForm,
  OnboardingStepType.ConnectBilling,
  OnboardingStepType.ConnectPayroll,
  OnboardingStepType.WorkLocations,
  OnboardingStepType.ConnectBanking,
  OnboardingStepType.CompanyProfile,
  OnboardingStepType.CompleteKyc,
  OnboardingStepType.ConnectShifts,
  OnboardingStepType.SetLaunchDate,
] as const

export const isAssignableHealthcheck = (value: string): value is OnboardingStepType =>
  ORDERED_ONBOARDING_STEP_TYPES.includes(value as OnboardingStepType)

/**
 * Finds the assignee for a specific healthcheck step
 * @param onboardingWorkflow - The onboarding workflow data
 * @param healthcheckKey - The healthcheck key (e.g., 'billing', 'payroll')
 * @returns The assigned user or null if no assignment found
 */
const findStepAssignee = (
  onboardingWorkflow: OnboardingWorkflowFragment | null | undefined,
  stepType: OnboardingStepType
): UserFragment | null => {
  if (!onboardingWorkflow?.steps) {
    return null
  }

  const step = onboardingWorkflow.steps.find((step) => step.stepType === stepType)
  return step?.assignee || null
}

/**
 * Formats a user's display name for UI display
 * @param user - The user object
 * @returns Formatted display name
 */
const formatAssigneeDisplayName = (user: UserFragment): string => {
  const firstName = user.firstName?.trim()
  const lastName = user.lastName?.trim()

  if (firstName && lastName) {
    return `${firstName} ${lastName[0]}`
  }

  if (firstName) {
    return firstName
  }

  if (lastName) {
    return lastName
  }

  // Fallback to email if no name is available
  return user.email || 'Unknown User'
}

/**
 * Creates assignee information for display in healthcheck items
 * @param user - The assigned user
 * @returns Formatted assignee information
 */
const createAssigneeInfo = (user: UserFragment) => {
  return {
    id: user.id,
    name: formatAssigneeDisplayName(user),
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  }
}

/**
 * Gets assignee information for a healthcheck key
 * @param onboardingWorkflow - The onboarding workflow data
 * @param healthcheckKey - The healthcheck key
 * @returns Assignee info or null if no assignment
 */
export const getOnboardingStepAssignee = (
  onboardingWorkflow: OnboardingWorkflowFragment | null | undefined,
  stepType: OnboardingStepType
) => {
  const assignee = findStepAssignee(onboardingWorkflow, stepType)
  return assignee ? createAssigneeInfo(assignee) : null
}

export const getStep = (
  onboardingWorkflow: OnboardingWorkflowFragment | null | undefined,
  stepType: OnboardingStepType
) => {
  return onboardingWorkflow?.steps.find((step) => step.stepType === stepType) || null
}

const ONBOARDING_HEALTHCHECK_STATUS: Record<OnboardingStatus, HealthcheckStatus> = {
  [OnboardingStatus.Blocked]: 'incomplete',
  [OnboardingStatus.Completed]: 'complete',
  [OnboardingStatus.InProgress]: 'pending',
  [OnboardingStatus.NotStarted]: 'incomplete',
}
export const getHealthcheckStatus = (step: OnboardingStepFragment | null | undefined): HealthcheckStatus => {
  return step ? ONBOARDING_HEALTHCHECK_STATUS[step.status] : 'pending'
}

/**
 * Helper function to get the route path for a step type
 */
export const getStepRoutePath = (stepType: OnboardingStepType): string => {
  return STEP_ROUTE_MAPPING[stepType]
}

/**
 * Helper function to build full onboarding route path
 */
export const buildOnboardingPath = (
  orgId: number | string | undefined,
  stepType?: OnboardingStepType
): string => {
  if (!orgId) return '/onboard'
  const basePath = `/manage/org/${orgId}/onboard`
  if (stepType) {
    return `${basePath}/${getStepRoutePath(stepType)}`
  }
  return basePath
}

/**
 * Helper function to check if a path is an onboarding path
 */
export const isOnboardingPath = (pathname: string): boolean => {
  return pathname.includes('/onboard/')
}

/**
 * Helper function to extract step type from onboarding path
 */
export const getStepTypeFromPath = (pathname: string): OnboardingStepType | null => {
  const match = pathname.match(/\/onboard\/([^/]+)/)
  if (!match) return null

  const routePath = match[1]
  const stepConfigs = getAllStepConfigs()
  const config = stepConfigs.find((c) => c.routePath === routePath)

  return config?.stepType || null
}

const getAvailableStepConfigs = (
  org: OrganizationDetailFragment,
  allStepConfigs: OnboardingStepConfig[]
): OnboardingStepConfig[] => {
  if (!org.onboardingWorkflow) return []
  return allStepConfigs.filter((config) => getStep(org.onboardingWorkflow, config.stepType))
}

/**
 * Determine the step navigation state for the current organization
 * - Required steps will vary by organization, so we need to figure out which steps are required
 *   based on the organization.onboardingWorkflow.steps
 * - From that info, we can determine what the 'next' and 'previous' steps are
 */
export const useStepNavigationState = (org: OrganizationDetailFragment | null | undefined) => {
  const location = useLocation()
  const allStepConfigs = getAllStepConfigs()
  const availableStepConfigs = org ? getAvailableStepConfigs(org, allStepConfigs) : allStepConfigs
  const stepType = getStepTypeFromPath(location.pathname)
  const currentStep = stepType ? getStep(org?.onboardingWorkflow, stepType) : null
  return {
    stepConfigs: availableStepConfigs,
    currentStep,
    nextStep: getNextStepConfig(availableStepConfigs, stepType),
    previousStep: getPreviousStepConfig(availableStepConfigs, stepType),
  }
}

/**
 * Helper function to get the next step in sequence
 */
export const getNextStepConfig = (
  stepConfigs: OnboardingStepConfig[],
  currentStepType: OnboardingStepType | null
): OnboardingStepConfig | null => {
  if (!currentStepType) return null
  const currentIndex = stepConfigs.findIndex((config) => config.stepType === currentStepType)

  // if current step is not found or is the last step, return null - there is no next step
  if (currentIndex === -1 || currentIndex === stepConfigs.length - 1) return null

  return stepConfigs[currentIndex + 1] || null
}

/**
 * Helper function to get the previous step in sequence
 */
export const getPreviousStepConfig = (
  stepConfigs: OnboardingStepConfig[],
  currentStepType: OnboardingStepType | null
): OnboardingStepConfig | null => {
  if (!currentStepType) return null
  const currentIndex = stepConfigs.findIndex((config) => config.stepType === currentStepType)

  // if current step is not found or is the first step, return null - there is no previous step
  if (currentIndex <= 0) return null

  return stepConfigs[currentIndex - 1] || null
}
